import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import useContact from 'flow/hooks/useContact';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import { getCountryCode } from 'flow/utils/extension-utils';
import { getQueueCallDisplayText } from 'flow/utils/pbx-utils';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import QueuePositionWithTime from 'flow/components/ui-components/QueuePositionWithTime';
import OmnichannelIcon from 'flow/components/ui-components/OmnichannelIcon';
import { QueueCallWithExtraData } from 'flow/types/Pbx';
import { TypedMap } from 'flow/types/TypedMap';
import PbxIncomingCallPickupButton from './PbxIncomingCallPickupButton';
import { lookupNumber } from 'flow/actions/contact-actions';

interface Props {
    queueItem: TypedMap<QueueCallWithExtraData>;
    queuePosition?: number;
}

const PbxIncomingCallItem = ({ queueItem, queuePosition }: Props) => {
    const { i18next } = useAppContext();
    const { ownContact } = useContact();
    const dispatch = useAppDispatch();
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const omniTicketData = queueItem?.get('telavoxOmniTicketData');
    const isCentionOmniErrand = queueItem?.get('isOmnichannelErrand');

    const callerNumber = isCentionOmniErrand
        ? queueItem.get('source')
        : queueItem.getIn(['caller', 'number']) ||
          queueItem.get('OriginalCallerID') ||
          queueItem.get('Source');

    const contact = useAppSelector((state) => {
        const getContactDataForNumber = makeGetContactDataForNumber();
        //@ts-ignore
        return getContactDataForNumber(state, callerNumber);
    });

    const loggedInUserCountryCode = getCountryCode(ownContact, null);
    const callerContact = queueItem.get('callerContact') || contact;
    const callerNameOrNumber = getQueueCallDisplayText(
        callerNumber,
        callerContact,
        loggedInUserCountryCode,
        i18next,
    );

    useEffect(() => {
        if (!callerContact && !isCentionOmniErrand) {
            dispatch(lookupNumber(callerNumber));
        }
    }, [callerNumber]);

    const getBody = () => {
        if (isCentionOmniErrand) {
            return (
                <div className="flex h-9 flex-1 flex-col justify-between text-base/5 text-gray-800">
                    <span className="h-4 text-sm">
                        {queueItem?.get('omnichannelErrandSender')}
                    </span>
                    <span className="line-clamp-1 text-xs text-gray-500">
                        {queueItem?.get(
                            'omnichannelErrandUserFriendlyDescription',
                        )}
                    </span>
                </div>
            );
        }

        if (omniTicketData) {
            return (
                <div className="flex flex-1 justify-between text-base/5 text-gray-800">
                    {prettyFormatE164Number(
                        omniTicketData.get('sender'),
                        ownCountryCode,
                    )}
                </div>
            );
        }

        return (
            <div className="flex flex-1 flex-col">
                <div className="text-base/5 text-gray-800">
                    {callerNameOrNumber}
                </div>
                <div className="text-sm/4 text-gray-500">
                    {queueItem?.get('queueDescription')}
                </div>
            </div>
        );
    };

    return (
        <div className="flex w-full items-center justify-center gap-x-2 rounded-xs p-1">
            <div className="flex size-9 items-center justify-center rounded-md bg-gray-200 p-1 text-gray-600">
                {omniTicketData || isCentionOmniErrand ? (
                    <OmnichannelIcon
                        channelType={
                            omniTicketData?.get('channel') ||
                            queueItem?.get('omnichannelErrandChannelType')
                        }
                    />
                ) : (
                    <Icon iconKey={'phone'} />
                )}
            </div>
            {getBody()}
            <PbxIncomingCallPickupButton queueItem={queueItem} />

            <div className="flex flex-col text-end text-xs">
                <QueuePositionWithTime
                    number={queuePosition ?? queueItem.get('queuePosition')}
                    joinTime={queueItem.get('joinTime')}
                />
            </div>
        </div>
    );
};

export default PbxIncomingCallItem;
